import React from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, Trash2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useFmt } from "../context/CurrencyContext";
import { resolveImageUrl } from "../components/ImageUpload";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal } = useCart();
  const fmtJMD = useFmt();

  if (items.length === 0)
    return (
      <div className="max-w-3xl mx-auto px-5 sm:px-6 py-16 sm:py-24 text-center" data-testid="cart-empty">
        <div className="overline text-zinc-500 mb-3">Your cart</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Nothing here yet</h1>
        <p className="mt-4 text-zinc-600">Start your box — it takes 30 seconds.</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3 sm:gap-4">
          <Link to="/build" className="btn-primary">Build Yuh Own Box</Link>
          <Link to="/boxes" className="btn-ghost">Preset Boxes</Link>
        </div>
      </div>
    );

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-6 md:px-12 py-12 sm:py-16" data-testid="cart-page">
      <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mb-8 sm:mb-10">Your cart</h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
        <div className="lg:col-span-2 space-y-4">
          {items.map((it) => (
            <div key={it.key} className="flex flex-wrap sm:flex-nowrap items-center gap-3 sm:gap-4 p-4 rounded-2xl border border-zinc-100 bg-white" data-testid={`cart-item-${it.key}`}>
              {it.image_url ? (
                <img src={resolveImageUrl(it.image_url)} alt={it.name} className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl object-cover flex-shrink-0" />
              ) : (
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl bg-zinc-100 flex items-center justify-center flex-shrink-0">
                  <span className="font-display font-bold text-zinc-400 text-xs">BOX</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm sm:text-base break-words">{it.name}</div>
                {it.custom_flavours && it.custom_flavours.length > 0 && (
                  <div className="text-xs text-zinc-500 mt-1 break-words">
                    {Object.entries(it.custom_flavours.reduce((a, n) => ((a[n] = (a[n] || 0) + 1), a), {}))
                      .map(([n, c]) => `${c}× ${n}`).join(" · ")}
                  </div>
                )}
                <div className="text-xs sm:text-sm text-zinc-500 mt-1">{fmtJMD(it.price)} each</div>
              </div>
              <div className="flex items-center justify-between sm:justify-end gap-3 w-full sm:w-auto sm:flex-shrink-0 mt-2 sm:mt-0">
                <div className="flex items-center gap-2 border border-zinc-200 rounded-full px-2">
                  <button onClick={() => updateQty(it.key, it.quantity - 1)} className="p-2" data-testid={`cart-dec-${it.key}`}><Minus size={12} /></button>
                  <span className="w-6 text-center font-semibold text-sm">{it.quantity}</span>
                  <button onClick={() => updateQty(it.key, it.quantity + 1)} className="p-2" data-testid={`cart-inc-${it.key}`}><Plus size={12} /></button>
                </div>
                <div className="font-display font-bold text-sm sm:text-base sm:w-24 text-right whitespace-nowrap">{fmtJMD(it.price * it.quantity)}</div>
                <button onClick={() => removeItem(it.key)} className="p-2 text-zinc-400 hover:text-red-500 flex-shrink-0" data-testid={`cart-remove-${it.key}`}>
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
        <div>
          <div className="rounded-2xl border border-zinc-200 bg-white p-5 sm:p-6 lg:sticky lg:top-24">
            <div className="overline text-zinc-500 mb-3">Summary</div>
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-zinc-600">Subtotal</span>
              <span className="font-semibold">{fmtJMD(subtotal)}</span>
            </div>
            <div className="flex items-center justify-between text-sm mb-4">
              <span className="text-zinc-600">Delivery</span>
              <span className="text-zinc-400">calculated at checkout</span>
            </div>
            <div className="border-t border-zinc-200 pt-4 flex items-center justify-between">
              <span className="font-semibold">Total</span>
              <span className="font-display text-2xl font-bold">{fmtJMD(subtotal)}</span>
            </div>
            <Link to="/checkout" className="btn-primary w-full inline-flex justify-center mt-5" data-testid="go-to-checkout">
              Checkout
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
